/**
 * KUSULM PREMIUM FEATURES
 * Electronics / Home / Beauty / Toys store — UAE
 *
 * Features included:
 * 1.  Variant image switch on swatch click
 * 2.  Hover zoom (magnifier lens)
 * 3.  Quick view modal (fetch + inject)
 * 4.  Smart section visibility (mobile/desktop/scroll-depth)
 * 5.  Blur-up adaptive image loading (progressive)
 * 6.  Exit-intent & idle discount popup
 * 7.  Scroll-based UI transformation (header compact)
 * 8.  Intent-based CTA highlight
 * 9.  Micro-delay stagger animations
 * 10. Inline variant UX upgrade (pills + hover preview)
 * 11. Cart count bounce animation
 * 12. Psychological pricing display (save amount in Dhs.)
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
     UTILS
  ───────────────────────────────────────── */
  var raf = window.requestAnimationFrame || function(fn) { setTimeout(fn, 16); };

  function debounce(fn, ms) {
    var t;
    return function() {
      clearTimeout(t);
      t = setTimeout(fn.bind(this, arguments), ms);
    };
  }

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  /* ─────────────────────────────────────────
     1. VARIANT IMAGE SWITCH
     When a swatch/variant option changes, swap
     the main product image to the variant image.
  ───────────────────────────────────────── */
  function initVariantImageSwitch() {
    document.addEventListener('change', function(e) {
      var input = e.target;
      if (!input.matches('[data-option-selector], [name^="options"]')) return;

      var form = input.closest('form') || input.closest('[data-product-form]');
      if (!form) return;

      // Try to find the matching variant select
      var variantSelect = form.querySelector('[data-variant-select], select[name="id"]');
      if (!variantSelect) return;

      var variantId = variantSelect.value;
      if (!variantId) return;

      // Check Shopify's product JSON (set globally or via meta tag)
      var productData = window.__kusulmProductData || {};
      var variant = (productData.variants || []).find(function(v) {
        return String(v.id) === String(variantId);
      });

      if (variant && variant.featured_image) {
        var mediaSlides = document.querySelectorAll('.product-media-wrapper .product__media.media');
        if (mediaSlides.length > 0) {
          var targetImg = mediaSlides[0].querySelector('img');
          if (targetImg) {
            var newSrc = variant.featured_image.src;
            // Blur-up transition
            targetImg.style.transition = 'opacity 0.25s ease';
            targetImg.style.opacity = '0.4';
            targetImg.src = newSrc;
            targetImg.onload = function() {
              targetImg.style.opacity = '1';
            };
          }
        }
      }

      // Highlight matching thumbnail
      var thumbs = document.querySelectorAll('.thumbnail-slider .thumbnail, .product-thumbnail img');
      if (variant && variant.featured_image) {
        thumbs.forEach(function(thumb) {
          var src = thumb.getAttribute('src') || '';
          var mediaId = thumb.closest('[data-media-id]');
          if (mediaId && String(mediaId.dataset.mediaId) === String(variant.featured_image.id)) {
            mediaId.click();
          }
        });
      }
    });
  }

  /* ─────────────────────────────────────────
     2. CLICK ZOOM (magnifier lens on product images)
  ───────────────────────────────────────── */
  function initHoverZoom() {
    var style = document.createElement('style');
    style.textContent = [
      '.kusulm-zoom-wrap{position:relative;overflow:hidden;display:block;}',
      '.kusulm-zoom-lens{position:absolute;border:2px solid rgba(255,255,255,.7);border-radius:50%;width:100px;height:100px;pointer-events:none;display:none;z-index:10;',
      'box-shadow:0 4px 20px rgba(0,0,0,.15);background:rgba(255,255,255,.1);transform:translate(-50%,-50%);}',
      '.kusulm-zoom-result{position:fixed;top:50%;left:55%;transform:translateY(-50%);',
      'width:340px;height:340px;border-radius:12px;overflow:hidden;display:none;z-index:9999;',
      'box-shadow:0 24px 60px rgba(0,0,0,.2);pointer-events:none;border:1px solid rgba(0,0,0,.08);background:#fff;}',
      '.kusulm-zoom-result img{position:absolute;max-width:none;pointer-events:none;}',
      '.kusulm-zoom-btn{position:absolute !important;top:auto !important;bottom:12px !important;right:12px !important;width:34px;height:34px;border-radius:6px;background:rgba(255,255,255,0.92);',
      'border:1px solid rgba(0,0,0,0.1);box-shadow:0 4px 12px rgba(0,0,0,0.1);display:flex;align-items:center;justify-content:center;',
      'cursor:pointer;z-index:99;transition:all 0.2s;color:#000;opacity:0.9;}',
      '.kusulm-zoom-btn:hover{transform:scale(1.1);background:#fff;opacity:1;}',
      '.kusulm-zoom-btn svg{width:18px;height:18px;}',
      '.kusulm-zoom-btn.is-active{background:#000;color:#fff;bottom:12px !important;right:12px !important;}',
      '@media(max-width:989px){.kusulm-zoom-wrap:not(.force-zoom){cursor:default;}',
      '.kusulm-zoom-result,.kusulm-zoom-lens,.kusulm-zoom-btn{display:none!important;}}'
    ].join('');
    document.head.appendChild(style);

    var result = document.createElement('div');
    result.className = 'kusulm-zoom-result';
    var resultImg = document.createElement('img');
    result.appendChild(resultImg);
    document.body.appendChild(result);

    var lens = document.createElement('div');
    lens.className = 'kusulm-zoom-lens';
    document.body.appendChild(lens);

    function attachZoom(wrap) {
      if (wrap.dataset.zoomInit) return;
      wrap.dataset.zoomInit = '1';

      var img = wrap.querySelector('.product__media img, img.zoomable, .product-media-gallery__item img');
      if (!img) return;

      var zoomSrc = img.dataset.zoomSrc || img.src.replace(/width=\d+/, 'width=1600');
      var isZooming = false;

      // Create magnifier button
      var btn = document.createElement('button');
      btn.className = 'kusulm-zoom-btn';
      btn.setAttribute('aria-label', 'Toggle Zoom');
      btn.innerHTML = '<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>';
      wrap.appendChild(btn);

      function closeZoom() {
        isZooming = false;
        lens.style.display = 'none';
        result.style.display = 'none';
        btn.classList.remove('is-active');
        btn.innerHTML = '<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>';
      }

      function openZoom() {
        isZooming = true;
        var zi = new Image();
        zi.src = zoomSrc;
        zi.onload = function() {
          if (!isZooming) return;
          resultImg.src = zoomSrc;
          lens.style.display = 'block';
          result.style.display = 'block';
          btn.classList.add('is-active');
          btn.innerHTML = '<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>'; // X mark
        };
      }

      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (window.innerWidth < 990) return;
        
        if (isZooming) closeZoom();
        else openZoom();
      });

      wrap.addEventListener('mouseleave', function() {
        if (isZooming) closeZoom();
      });

      wrap.addEventListener('mousemove', function(e) {
        if (!isZooming || window.innerWidth < 990) return;
        var rect = wrap.getBoundingClientRect();
        var rx = e.clientX - rect.left;
        var ry = e.clientY - rect.top;
        var ratioX = rx / rect.width;
        var ratioY = ry / rect.height;

        lens.style.left = e.clientX + 'px';
        lens.style.top  = e.clientY + 'px';

        var rw = result.offsetWidth;
        var rh = result.offsetHeight;
        var iw = resultImg.naturalWidth  || 1600;
        var ih = resultImg.naturalHeight || 1600;
        var scale = Math.max(rw / rect.width, rh / rect.height) * 2.2;

        resultImg.style.width  = (rect.width  * scale) + 'px';
        resultImg.style.height = (rect.height * scale) + 'px';
        resultImg.style.left   = (-ratioX * rect.width  * scale + rw / 2) + 'px';
        resultImg.style.top    = (-ratioY * rect.height * scale + rh / 2) + 'px';
      });
    }

    function scanForZoomTargets() {
      // Add relative positioning rule to parents if not present
      var targets = document.querySelectorAll('.product-media-wrapper .product__media, .product-media-gallery__item');
      targets.forEach(function(el) {
        el.classList.add('kusulm-zoom-wrap');
        attachZoom(el);
      });
    }

    ready(scanForZoomTargets);
    document.addEventListener('shopify:section:load', scanForZoomTargets);
  }

  /* ─────────────────────────────────────────
     3. QUICK VIEW MODAL
  ───────────────────────────────────────── */
  function initQuickView() {
    var modal = document.getElementById('KusulmQuickView');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'KusulmQuickView';
      modal.innerHTML = [
        '<div class="kqv-overlay" data-kqv-close></div>',
        '<div class="kqv-panel">',
          '<button class="kqv-close" data-kqv-close aria-label="Close">',
            '<svg width="18" height="18" viewBox="0 0 18 18" fill="none">',
              '<path d="M4 4l10 10M14 4L4 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
            '</svg>',
          '</button>',
          '<div class="kqv-body" id="KusulmQVBody">',
            '<div class="kqv-spinner"><svg viewBox="0 0 24 24" fill="none" width="32" height="32">',
              '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity=".2"/>',
              '<path d="M12 2a10 10 0 0110 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
            '</svg></div>',
          '</div>',
          '<a class="kqv-full-link" href="#" id="KusulmQVLink">View full product ›</a>',
        '</div>'
      ].join('');

      var s = document.createElement('style');
      s.textContent = [
        '#KusulmQuickView{display:none;position:fixed;inset:0;z-index:10000;align-items:center;justify-content:center;}',
        '#KusulmQuickView.is-open{display:flex;}',
        '.kqv-overlay{position:absolute;inset:0;background:rgba(0,0,0,.48);backdrop-filter:blur(4px);cursor:pointer;}',
        '.kqv-panel{position:relative;z-index:1;background:rgb(var(--color-background));border-radius:var(--media-radius,12px);',
        'width:min(90vw,860px);max-height:88vh;overflow:hidden;display:flex;flex-direction:column;',
        'box-shadow:0 32px 80px rgba(0,0,0,.22);}',
        '.kqv-close{position:absolute;top:12px;right:12px;z-index:2;width:36px;height:36px;display:flex;align-items:center;',
        'justify-content:center;background:rgba(var(--color-foreground),.06);border:none;border-radius:50%;cursor:pointer;color:rgb(var(--color-foreground));}',
        '.kqv-body{overflow-y:auto;flex:1;padding:1.6rem;}',
        '.kqv-spinner{display:flex;align-items:center;justify-content:center;min-height:200px;',
        'animation:kqv-spin .7s linear infinite;}',
        '@keyframes kqv-spin{to{transform:rotate(360deg)}}',
        '.kqv-full-link{display:flex;align-items:center;justify-content:center;padding:12px;',
        'border-top:1px solid rgba(var(--color-foreground),.1);font-size:.8125rem;color:rgba(var(--color-foreground),.6);',
        'text-decoration:none;}',
        '.kqv-full-link:hover{color:rgb(var(--color-foreground));}'
      ].join('');
      document.head.appendChild(s);
      document.body.appendChild(modal);
    }

    var body = document.getElementById('KusulmQVBody');
    var link = document.getElementById('KusulmQVLink');

    function open(handle) {
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (link) link.href = '/products/' + handle;

      body.innerHTML = '<div class="kqv-spinner"><svg viewBox="0 0 24 24" fill="none" width="32" height="32"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity=".2"/><path d="M12 2a10 10 0 0110 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></div>';

      fetch('/products/' + handle + '?section_id=featured-product', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function(r) { return r.text(); })
        .then(function(html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var inner = doc.querySelector('.featured-product, [data-section-type="featured-product"], main > *:first-child');
          body.innerHTML = inner ? inner.outerHTML : '<p style="padding:2rem;text-align:center">Could not load product. <a href="/products/' + handle + '">View page →</a></p>';
          // Re-init variant pickers inside QV
          document.dispatchEvent(new CustomEvent('kusulm:qv-loaded', { detail: { container: body } }));
        })
        .catch(function() {
          body.innerHTML = '<p style="padding:2rem;text-align:center">Could not load. <a href="/products/' + handle + '">View product →</a></p>';
        });
    }

    function close() {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-quick-view], .quick-add-button[data-product-handle], [data-kusulm-qv]');
      if (btn) {
        e.preventDefault();
        var handle = btn.dataset.quickView || btn.dataset.productHandle || btn.dataset.kusulmQv;
        if (handle) open(handle);
        return;
      }
      if (e.target.closest('[data-kqv-close]') || e.target === modal) close();
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }

  /* ─────────────────────────────────────────
     4. SMART SECTION VISIBILITY
     [data-show-on="mobile|desktop|both"]
     [data-show-after-scroll="300"] (px)
     [data-show-if="logged-out|logged-in"]
  ───────────────────────────────────────── */
  function initSmartVisibility() {
    var isLoggedIn = document.body.classList.contains('customer-logged-in');
    var isMobile   = window.innerWidth < 990;
    var scrollY    = 0;

    var style = document.createElement('style');
    style.textContent = '[data-kusulm-hidden]{display:none!important;}';
    document.head.appendChild(style);

    function evaluate() {
      scrollY  = window.pageYOffset;
      isMobile = window.innerWidth < 990;

      document.querySelectorAll('[data-show-on],[data-show-after-scroll],[data-show-if]').forEach(function(el) {
        var show = true;

        var device = el.dataset.showOn;
        if (device === 'mobile'  && !isMobile)  show = false;
        if (device === 'desktop' &&  isMobile)  show = false;

        var afterScroll = parseInt(el.dataset.showAfterScroll || '0', 10);
        if (afterScroll && scrollY < afterScroll) show = false;

        var ifUser = el.dataset.showIf;
        if (ifUser === 'logged-in'  && !isLoggedIn) show = false;
        if (ifUser === 'logged-out' &&  isLoggedIn) show = false;

        el.toggleAttribute('data-kusulm-hidden', !show);
      });
    }

    evaluate();
    window.addEventListener('scroll', debounce(evaluate, 80), { passive: true });
    window.addEventListener('resize', debounce(evaluate, 200));
  }

  /* ─────────────────────────────────────────
     5. BLUR-UP ADAPTIVE IMAGE LOADING
     Adds blur placeholder, loads full image after
  ───────────────────────────────────────── */
  function initBlurUpImages() {
    var s = document.createElement('style');
    s.textContent = [
      '.kusulm-blur-wrap{position:relative;overflow:hidden;}',
      '.kusulm-blur-placeholder{position:absolute;inset:0;filter:blur(12px);transform:scale(1.05);',
      'transition:opacity .4s ease;object-fit:cover;width:100%;height:100%;}',
      '.kusulm-blur-main{transition:opacity .4s ease;}',
      '.kusulm-blur-main.loaded{opacity:1;}',
      '.kusulm-blur-main.loading{opacity:0;}'
    ].join('');
    document.head.appendChild(s);

    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        var img = entry.target;
        var hq  = img.dataset.kusulmHq;
        if (!hq) return;
        img.classList.add('loading');
        var loader = new Image();
        loader.onload = function() {
          img.src = hq;
          img.classList.remove('loading');
          img.classList.add('loaded');
          // fade out blur placeholder
          var p = img.parentElement.querySelector('.kusulm-blur-placeholder');
          if (p) p.style.opacity = '0';
        };
        loader.src = hq;
        io.unobserve(img);
      });
    }, { rootMargin: '200px' });

    function scanImages() {
      document.querySelectorAll('[data-kusulm-hq]').forEach(function(img) {
        io.observe(img);
      });
    }

    ready(scanImages);
  }

  /* ─────────────────────────────────────────
     6. EXIT INTENT + IDLE DISCOUNT POPUP
     [data-kusulm-offer-popup] — set on a div
     Shows after 20s idle OR exit intent (mouse top)
  ───────────────────────────────────────── */
  function initConversionPopups() {
    var popup = document.querySelector('[data-kusulm-offer-popup]');
    if (!popup) return;

    var shown   = false;
    var storageKey = 'kusulm_offer_shown';

    // Don't show again in same session
    if (sessionStorage.getItem(storageKey)) return;

    var s = document.createElement('style');
    s.textContent = [
      '[data-kusulm-offer-popup]{position:fixed;bottom:-100%;left:50%;transform:translateX(-50%);',
      'z-index:9998;transition:bottom .4s cubic-bezier(.16,1,.3,1);max-width:400px;width:calc(100% - 32px);}',
      '[data-kusulm-offer-popup].is-visible{bottom:24px;}',
      '[data-kusulm-offer-close]{cursor:pointer;background:none;border:none;}'
    ].join('');
    document.head.appendChild(s);

    function show() {
      if (shown) return;
      shown = true;
      sessionStorage.setItem(storageKey, '1');
      popup.classList.add('is-visible');
    }

    // Exit intent (mouse leaves top of viewport)
    document.addEventListener('mouseleave', function(e) {
      if (e.clientY < 10) show();
    });

    // Idle timer — 20s
    var idleTimer = setTimeout(show, 20000);
    ['mousemove','keydown','scroll','touchstart'].forEach(function(ev) {
      document.addEventListener(ev, function() {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(show, 20000);
      }, { passive: true, once: false });
    });

    // Close button
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-kusulm-offer-close]')) {
        popup.classList.remove('is-visible');
      }
    });
  }

  /* ─────────────────────────────────────────
     7. SCROLL-BASED HEADER TRANSFORMATION
     Makes header compact + adds bg on scroll
  ───────────────────────────────────────── */
  function initScrollHeaderTransform() {
    var header = document.querySelector('.section-header sticky-header, .section-header .header-wrapper');
    if (!header) return;

    var s = document.createElement('style');
    s.textContent = [
      '.kusulm-header-compact .header__heading-logo{max-width:100px!important;}',
      '.kusulm-header-compact .header{padding-top:8px!important;padding-bottom:8px!important;}',
      '.kusulm-header-scrolled sticky-header,.kusulm-header-scrolled .header-wrapper{',
      'background:rgba(var(--color-background),.96)!important;',
      'backdrop-filter:blur(12px)!important;',
      '-webkit-backdrop-filter:blur(12px)!important;',
      'box-shadow:0 2px 20px rgba(0,0,0,.08)!important;}',
      '.section-header{transition:all .35s cubic-bezier(.16,1,.3,1);}'
    ].join('');
    document.head.appendChild(s);

    var lastY = 0;
    var section = document.querySelector('.shopify-section-group-header-group');

    // Initial check on load
    function checkScroll() {
      var y = window.pageYOffset;
      if (!section) return;

      if (y < 20) {
        header.classList.add('k-header-at-top');
        section.classList.remove('k-header-scrolled');
      } else {
        header.classList.remove('k-header-at-top');
        section.classList.add('k-header-scrolled');
      }

      // Compact mode for extreme scroll (>400px)
      if (y > 400) {
        section.classList.add('kusulm-header-compact');
      } else {
        section.classList.remove('kusulm-header-compact');
      }
    }

    window.addEventListener('scroll', checkScroll, { passive: true });
    ready(checkScroll);
  }

  /* ─────────────────────────────────────────
     8. INTENT-BASED CTA HIGHLIGHT
     ATC button pulses when user lingers on product card
  ───────────────────────────────────────── */
  function initIntentCTA() {
    var s = document.createElement('style');
    s.textContent = [
      '@keyframes kusulm-cta-pulse{',
      '0%{box-shadow:0 0 0 0 rgba(var(--color-button),0.5)}',
      '70%{box-shadow:0 0 0 10px rgba(var(--color-button),0)}',
      '100%{box-shadow:0 0 0 0 rgba(var(--color-button),0)}}',
      '.kusulm-cta-intent .product-form__submit,.kusulm-cta-intent .btn-add-to-cart{',
      'animation:kusulm-cta-pulse 1s ease 2!important;}',
      '.card-wrapper:hover .card__badge{transform:scale(1.05);transition:transform .2s;}',
    ].join('');
    document.head.appendChild(s);

    var hoverTimers = new WeakMap();

    document.addEventListener('mouseover', function(e) {
      var card = e.target.closest('.card-wrapper, .product-card-wrapper');
      if (!card) return;
      if (hoverTimers.has(card)) return;
      var t = setTimeout(function() {
        card.classList.add('kusulm-cta-intent');
      }, 1200); // 1.2s hover → pulse
      hoverTimers.set(card, t);
    });

    document.addEventListener('mouseout', function(e) {
      var card = e.target.closest('.card-wrapper, .product-card-wrapper');
      if (!card) return;
      var t = hoverTimers.get(card);
      if (t) { clearTimeout(t); hoverTimers.delete(card); }
      card.classList.remove('kusulm-cta-intent');
    });
  }

  /* ─────────────────────────────────────────
     9. MICRO-DELAY STAGGER ANIMATIONS
     Adds cascading entrance delays to section children
  ───────────────────────────────────────── */
  function initMicroDelays() {
    var s = document.createElement('style');
    s.textContent = [
      '[data-kusulm-stagger] > *{opacity:0;transform:translateY(16px);',
      'transition:opacity .5s cubic-bezier(.16,1,.3,1),transform .5s cubic-bezier(.16,1,.3,1);}',
      '[data-kusulm-stagger].in-view > *{opacity:1;transform:translateY(0);}',
    ].join('');
    document.head.appendChild(s);

    // Inject stagger delays
    document.querySelectorAll('[data-kusulm-stagger]').forEach(function(section) {
      Array.from(section.children).forEach(function(child, i) {
        child.style.transitionDelay = (i * 80) + 'ms';
      });
    });

    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    document.querySelectorAll('[data-kusulm-stagger]').forEach(function(el) {
      io.observe(el);
    });
  }

  /* ─────────────────────────────────────────
     10. PSYCHOLOGICAL PRICING — show "Save Dhs. X"
     Replaces or supplements % badge with absolute saving
  ───────────────────────────────────────── */
  function initPsychoPricing() {
    function formatDhs(cents) {
      var v = cents / 100;
      return 'Dhs. ' + (Number.isInteger(v) ? v : v.toFixed(2));
    }

    function processPriceEl(el) {
      if (el.dataset.priceDone) return;
      el.dataset.priceDone = '1';

      var saleEl    = el.querySelector('.price-item--sale');
      var regularEl = el.querySelector('.price-item--regular');
      if (!saleEl || !regularEl) return;

      var saleCents = parseInt(saleEl.dataset.priceAmount || '0', 10);
      var regCents  = parseInt(regularEl.dataset.priceAmount || '0', 10);
      if (!saleCents || !regCents || regCents <= saleCents) return;

      var saveCents = regCents - saleCents;
      var existing  = el.querySelector('.kusulm-save-badge');
      if (!existing) {
        var badge = document.createElement('span');
        badge.className = 'kusulm-save-badge';
        badge.textContent = 'Save ' + formatDhs(saveCents);
        badge.style.cssText = [
          'display:inline-flex;align-items:center;',
          'background:rgb(var(--color-sale-badge-background,200 39 45));',
          'color:rgb(var(--color-sale-badge-text,255 255 255));',
          'font-size:0.75rem;font-weight:700;padding:3px 8px;border-radius:100px;margin-left:6px;'
        ].join('');
        saleEl.insertAdjacentElement('afterend', badge);
      }
    }

    function scan() {
      document.querySelectorAll('.price--on-sale').forEach(processPriceEl);
    }

    ready(scan);
    document.addEventListener('variant:change', scan);
  }

  /* ─────────────────────────────────────────
     11. CART COUNT BOUNCE
  ───────────────────────────────────────── */
  function initCartBounce() {
    document.addEventListener('cart:updated', function() {
      document.querySelectorAll('.cart-count-bubble').forEach(function(b) {
        b.classList.remove('animated');
        void b.offsetWidth;
        b.classList.add('animated');
        setTimeout(function() { b.classList.remove('animated'); }, 500);
      });
    });
  }

  /* ─────────────────────────────────────────
     12. PRELOAD KEY ELEMENTS (speed hack)
     Preloads first product image & hero image
  ───────────────────────────────────────── */
  function initPreload() {
    // Preload hero / banner first image
    var heroImg = document.querySelector('.banner__media img, .hero img, .slideshow__slide img');
    if (heroImg && heroImg.src) {
      var link = document.createElement('link');
      link.rel = 'preload';
      link.as  = 'image';
      link.href = heroImg.src;
      document.head.appendChild(link);
    }
  }

  /* ─────────────────────────────────────────
     13. ETHEREAL RISE SCROLL REVEAL
     Animates sections/cards as they enter view
  ───────────────────────────────────────── */
  function initScrollReveal() {
    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    function scan() {
      // 1. Standard reveal
      var items = document.querySelectorAll('.shopify-section:not(.header-section), .product-item, .collection-item, .blog-post-card, .ethereal-rise, .ethereal-rise-stagger');
      items.forEach(function(el) {
        if (!el.classList.contains('reveal-init')) {
          el.classList.add('reveal-init');
          observer.observe(el);
        }
      });
    }

    ready(scan);
    document.addEventListener('shopify:section:load', scan);
  }

  /* ─────────────────────────────────────────
     INIT ALL
  ───────────────────────────────────────── */
  initVariantImageSwitch();
  //initHoverZoom();
  initQuickView();
  initSmartVisibility();
  initBlurUpImages();
  initConversionPopups();
  initScrollHeaderTransform();
  initIntentCTA();
  initMicroDelays();
  initPsychoPricing();
  initCartBounce();
  initPreload();
  initScrollReveal();

  // Expose for external use
  window.KUSULM_PREMIUM = {
    reinit: function(container) {
      initSmartVisibility();
      initMicroDelays();
      initPsychoPricing();
    }
  };

})();
